import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Button, Card, CardBody, Collapse, Alert, ModalFooter } from "reactstrap";

import { useGetPolicyNudgeHistory } from "../../hooks/company-policy/useCompanyPolicyNudgeHistory";
import { CompanyPolicyNudgeHistory } from "../../interfaces/models/CompanyPolicyNudgeHistory";
import Loading from "../Loading";
import moment from "moment";
import { CompanyPolicyNudgeHistoryActionOptions, DateFormat } from "../../helper/constants";
import toast from "react-hot-toast";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  policyId?: string;
  nudgeId?: string;
}

export default function CompanyPolicyAndNudgeHistoryModal({ isOpen, toggle, policyId, nudgeId }: Props) {
  const [history, setHistory] = useState<CompanyPolicyNudgeHistory[]>([]);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [reloading, setReloading] = useState(false);
  const maxLimitPerPage = 5;
  const [openId, setOpenId] = useState(null);

  const { data, loading, error, refetch} = useGetPolicyNudgeHistory(page, policyId, nudgeId);

  useEffect(() => {
    if (data) {
      const historyRaw = data.company_policy_nudge_history as CompanyPolicyNudgeHistory[];
      if(historyRaw.length === 0) {
        setIsLastPage(true);
        toast.success("You've reach the end of the history");
      } else {
        setHistory(prev => [...prev, ...historyRaw]);
      }
    }
  }, [data]);

  
  const handleLoadMore = async () => {
    setReloading(true);
    await refetch({ page, policyId, nudgeId});
    setReloading(false);
    setPage((prev) => prev + 1);
  }


  const toggleDetails = (id: string) => {
    setOpenId(openId === id ? null : id);
  };
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3" className="mb-0">
        History Timeline
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">Oops, we’re having trouble getting the History. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
        <div className="timeline">
          {history.map(item => (
            <div key={item._id} className="timeline-item mb-4">
              <div className="d-flex align-items-start">
                <div className={`timeline-dot mr-3 mt-2 bg-${CompanyPolicyNudgeHistoryActionOptions[item.action_type - 1].color}`}></div>

                <div className="flex-grow-1">
                  <div>
                    <strong className={`text-${CompanyPolicyNudgeHistoryActionOptions[item.action_type - 1].color}`}>{CompanyPolicyNudgeHistoryActionOptions[item.action_type - 1].name}</strong>
                    <span> a </span>
                    <span>{item.policy_nudge_id ? "Nudge": "Policy"} named <strong>{item.log_item_name}</strong></span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <small className="text-muted">{moment(item.created_at).local().format(`${DateFormat} hh:mm a`)}</small>
                    <small className="text-muted"> By: {item.created_by_user?.name}</small>
                  </div>
                  <div className="mt-2">
                    <Button size="sm" color="link" onClick={() => toggleDetails(item._id)}>
                      {openId === item._id ? "Hide Changes" : "View Changes"}
                    </Button>
                  </div>
                  <Collapse isOpen={openId === item._id}>
                    <Card className="mt-2">
                      <CardBody>
                        {
                          item.changes.map((change, index) => (
                            <div key={index}>
                              <p>
                                <strong>Field:</strong> {change.field_name}
                              </p>
                              <p>
                                <strong>Value:</strong> {change.value || <span className="text-muted">No Changes</span>}
                              </p>
                              <hr />
                            </div>
                          ))
                        }
                        
                      </CardBody>
                    </Card>
                  </Collapse>
                </div>
              </div>
            </div>
          ))}
        </div>
        {(loading || reloading) && <Loading />}
        {
          (!loading && !reloading) && !isLastPage && history.length >= maxLimitPerPage &&
          <div className="text-center" role="button">
            <span onClick={handleLoadMore} className="text-primary">Load More</span>
          </div>
        }
      </ModalBody>
      <ModalFooter>
        <Button onClick={toggle}>Close</Button>
      </ModalFooter>
      <style>
        {`
          .timeline {
            position: relative;
            padding-left: 20px;
          }
          .timeline::before {
            content: '';
            position: absolute;
            top: 0;
            left: 10px;
            width: 2px;
            height: 100%;
            background: #dee2e6;
          }
          .timeline-item {
            position: relative;
          }
          .timeline-dot {
            width: 14px;
            height: 14px;
            border-radius: 50%;
            flex-shrink: 0;
          }
        `}
      </style>
    </Modal>
  );
}
